import { selectIsDecisionSubmitting, useMedicationRequestStore } from '@/lib/stores/medication-request'
import TextareaWithLabel from '../textarea'
import useMedRequestAction from '@/lib/api/med-requests/action'
import { useCurrentMedication } from '@/hooks/useCurrentMedication'
import { cn, isMpsMedRequest } from '@/lib/utils'
import type { MedRequest } from '@/types/data'
import { hasRemainingTreatmentsToDecide } from '@/lib/med-requests'
import useRejectOrFollowUp, { type PanelDecide } from '@/lib/api/med-requests/reject-or-follow-up'
import { useGoNextMedRequest } from '@/hooks/useGoNextMedRequest'
import { useState, useEffect } from 'react'
import SingleCombobox from '@/components/common/single-combobox'
import { useQuestionnaire } from '@/hooks/useQuestionnaire'
import { useUser } from '@clerk/clerk-react'
import { useScrAiSummary } from '@/hooks/scr-ai-summary'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import Message from '@/components/ui/message'
import usePostReplyToConversation from '@/hooks/usePostReplyToConversation'
import useGenerateConversationTemplate from '@/hooks/useGenerateConversationTemplate'
import AttachmentPreview from '@/components/common/AttachmentPreview'
import { toast } from '@/hooks/use-toast'
import MagicWandIcon from '@/components/ui/icons/magic-wand-icon'
import useGetConversationById from '@/hooks/useGetConversationById'
import { useFlag } from '@unleash/proxy-client-react'
import AiButton from '@/components/ui/ai-button'
import LoadingSpinner from '@/components/ui/icons/loading-spinner'
import CloseIcon from '@/components/ui/icons/close'
import { X } from 'lucide-react'
import { canSendMessages } from '@/lib/permissions'
import CharacterCounter from '@/components/common/character-counter'
import { Checkbox } from '@/components/ui/checkbox'

let generationAbortController = new AbortController()

const tabs = ['patient', 'cs-team']

type PatientViewProps = {
  prompt: string
  setPrompt: (prompt: string) => void
  isRejection?: boolean
  isGenerationViewActive: boolean
  setIsGenerationViewActive: (isGenerationViewActive: boolean) => void
  aiResponse: string
  setAiResponse: (aiResponse: string) => void
} & PanelDecisionProps

/** Set in the Clinical decision panel: the view records `target`'s rejection, and Cancel closes the panel. */
type PanelDecisionProps = {
  target?: MedRequest
  onDecide?: PanelDecide
  onCancel?: () => void
  /** Keeps Send off while something outside the view blocks the decision (e.g. unviewed patient files). */
  isSendDisabled?: boolean
}

export const PatientView = ({
  prompt,
  setPrompt,
  isRejection,
  isGenerationViewActive,
  setIsGenerationViewActive,
  aiResponse,
  setAiResponse,
  target,
  onDecide,
  onCancel,
  isSendDisabled
}: PatientViewProps) => {
  const canSendMessagesRole = canSendMessages()
  const {
    setSidebarView,
    notes,
    medicationDirection,
    contactMessage,
    setContactMessage,
    rejectionReason,
    setRejectionReason
  } = useMedicationRequestStore()

  const message = isRejection ? rejectionReason : contactMessage
  const setMessage = isRejection ? setRejectionReason : setContactMessage

  const [template, setTemplate] = useState('')

  const { submit, isPending } = useRejectOrFollowUp(onDecide)

  const medRequest = useCurrentMedication(true)
  // In the Clinical decision panel the view decides `target`, possibly another item of the order.
  const data = target ?? medRequest?.data
  const { templateOptions } = useQuestionnaire(data?.questionaire ?? '')
  const isPanel = !!onDecide
  const templateChoices = isRejection
    ? (templateOptions?.rejectionOptions ?? [])
    : [...(templateOptions?.fanOptions ?? []), ...(templateOptions?.onHoldOptions ?? [])]
  const selectedFan = !isRejection ? templateOptions?.fanOptions.find(option => option.value === template) : undefined

  // The opened request's summary, which only the page's own decision sends.
  const { prompt: scrExecutedPrompt, summary: scrSummaryResult } = useScrAiSummary({
    medicationRequestId: medRequest?.data?.id ?? ''
  })

  const isRejectionFanEnabled = useFlag('rejection-fan') || data?.rejectionConversationId
  const isFanFlagEnabled = useFlag('conversation-fan') || data?.latestFanConversationId

  // A chain runs through several mutations, between which `isPending` briefly drops.
  const isDecisionSubmitting = useMedicationRequestStore(selectIsDecisionSubmitting)
  const isDisabled =
    (!selectedFan && !message?.trim()) || isPending || isDecisionSubmitting || medRequest.data?.locked || isSendDisabled
  const [csNeedToContactPatient, setCsNeedToContactPatient] = useState(!!isRejection)
  // On MPS orders CS must always be emailed, so the flag is forced on and the checkbox locked.
  const isMps = isMpsMedRequest(data)
  const csContactPatient = isMps || csNeedToContactPatient

  const {
    mutateAsync: generateConversationTemplate,
    isPending: isAITemplateLoading,
    reset: resetMutationState
  } = useGenerateConversationTemplate({
    medRequestId: data?.id ?? '',
    abortController: generationAbortController,
    type: isRejection ? 'rejection' : 'hold'
  })

  const isSendButtonVisible = isGenerationViewActive || template

  const isTemplateDropdownHidden = templateChoices.length === 0 || isGenerationViewActive

  const changeToAIGenerationPage = async (generateWhileChangingView: boolean) => {
    setTemplate('')
    setIsGenerationViewActive(true)
    if (!generateWhileChangingView) return
    await handleGenerateConversationTemplate()
  }

  const handleGenerateConversationTemplate = async () => {
    try {
      const res = await generateConversationTemplate({ prompt })
      setMessage(res)
      setAiResponse(res)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Sorry, we couldn’t generate a template for you at this time.'
      })
    }
  }

  const handleCancelGeneratingTemplate = () => {
    resetMutationState()
    setIsGenerationViewActive(false)
    setAiResponse('')
    setMessage('')
    generationAbortController.abort()
    generationAbortController = new AbortController()
  }

  const handleSettingTemplate = (template: string) => {
    const fan = !isRejection && templateOptions?.fanOptions.find(option => option.value === template)
    setMessage(fan ? '' : template)
    setTemplate(template)
    setPrompt('')
  }

  // Shows the form instead of the opened request's (old) conversation while other items still need
  // deciding; read-only roles keep reading the conversation.
  const isDecidingRemainingTreatments =
    !isPanel && canSendMessagesRole && !!data && hasRemainingTreatmentsToDecide(data, isRejection ? 'reject' : 'hold')
  const isConversationShown = !isPanel && !isDecidingRemainingTreatments

  const { data: conversation } = useGetConversationById({
    conversationId: isRejection ? (data?.rejectionConversationId ?? '') : (data?.latestFanConversationId ?? ''),
    enabled: isConversationShown && !!(isRejection ? data?.rejectionConversationId : data?.latestFanConversationId)
  })

  if (!data) return null

  if (conversation && isConversationShown) {
    return <ConversationView prompt={prompt} isRejection={isRejection} setPrompt={setPrompt} />
  }

  const handleSubmit = () => {
    const reason = selectedFan ? `FAN consultation requested: ${selectedFan.label}` : message.trim()

    submit(
      data,
      isRejection
        ? {
            id: data.id,
            action: 'reject',
            startConversation: !!isRejectionFanEnabled,
            reason,
            additionalNotes: notes,
            extraDirectionsForMedication: medicationDirection,
            scrExecutedPrompt: scrExecutedPrompt,
            scrSummaryResult: scrSummaryResult,
            csNeedToContactPatient: csContactPatient
          }
        : {
            id: data.id,
            action: 'hold',
            reason,
            startConversation: !!isFanFlagEnabled,
            scrSummaryResult,
            scrExecutedPrompt
          },
      selectedFan?.value.slice(4)
    )
  }

  const handleCancel = () => {
    setMessage('')
    if (onCancel) return onCancel()
    setSidebarView('navigation-menu')
  }
  return (
    <>
      {!canSendMessagesRole && (
        <div className='py-4 px-6 bg-[#FFF9E6] rounded-3xl border border-[#F4D03F] mb-4'>
          <p className='text-sm leading-[170%] text-[#B7950B] font-normal'>
            You have read-only access. You cannot{' '}
            {isRejection ? 'reject medication requests' : 'send follow-up messages'} or submit actions.
          </p>
        </div>
      )}
      {!isGenerationViewActive && canSendMessagesRole && (
        <>
          {!isTemplateDropdownHidden && (
            <>
              <p className='mb-4 text-[#1D1D1D] font-medium leading-[170%]'>Select a template</p>
              <div className='mb-4'>
                <SingleCombobox
                  isDisabled={isTemplateDropdownHidden || !canSendMessagesRole || isPending}
                  title='Templates'
                  defaultValue={template}
                  onValueChange={value => canSendMessagesRole && handleSettingTemplate(value)}
                  options={templateChoices}
                />
              </div>
              {!template && (
                <div className='flex items-center mb-4 gap-4'>
                  <div className='flex-1 h-px bg-[#D1D9E2]' />
                  <span className=' text-[#1D1D1D] text-sm font-medium leading-[170%]'>Or</span>
                  <div className='flex-1 h-px bg-[#D1D9E2]' />
                </div>
              )}
            </>
          )}
          {!isPanel && (isTemplateDropdownHidden || !template) && (
            <p className='mb-4 text-[#1D1D1D] font-medium leading-[170%]'>Generate a template</p>
          )}
        </>
      )}
      {template && !selectedFan && canSendMessagesRole && (
        <div className='relative'>
          <TextareaWithLabel
            className='h-[177px] rounded-2xl resize-none pt-3'
            placeholder={template ? '' : 'Add your reason'}
            value={message}
            onChange={e => canSendMessagesRole && setMessage(e.target.value)}
            disabled={!canSendMessagesRole}
          />
          <button
            type='button'
            data-testid='clear-template'
            onClick={() => setTemplate('')}
            className='absolute bottom-3 right-3 flex items-center justify-center rounded-full bg-gray-100 p-2 text-xs text-gray-700 group transition-all'
          >
            <CloseIcon className='w-4 h-4' />
            <span className='absolute right-full mr-2 bg-gray-100 px-2 py-1 rounded text-xs text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none'>
              Clear template
            </span>
          </button>
        </div>
      )}

      {!template && canSendMessagesRole && (
        <div className='mb-4 rounded-2xl border border-[#E9EAED] p-4'>
          {isPanel && <p className='mb-2 text-[#1D1D1D] font-medium leading-[170%]'>Generate a template</p>}
          <p className='mb-4 text-sm leading-[170%] text-[#4E545E] '>
            {isGenerationViewActive
              ? 'You can edit the generated template'
              : template
                ? 'No suitable template? Generate one!'
                : isPanel
                  ? 'Enter text to generate a response for the patient'
                  : 'Please enter a text to generate a response for the patient'}
          </p>
          <div className='relative'>
            <TextareaWithLabel
              className='h-[177px] rounded-2xl resize-none pt-3 mb-4'
              placeholder='Add your prompt'
              disabled={isAITemplateLoading}
              value={isGenerationViewActive && aiResponse ? message : prompt}
              onChange={e =>
                isGenerationViewActive && aiResponse ? setMessage(e.target.value) : setPrompt(e.target.value)
              }
            />
            {isGenerationViewActive && aiResponse && (
              <button
                type='button'
                data-testid='clear-template'
                onClick={() => {
                  setIsGenerationViewActive(false)
                }}
                className='absolute bottom-3 right-3 flex items-center justify-center rounded-full bg-gray-100 p-2 text-xs text-gray-700 group transition-all'
              >
                <CloseIcon className='w-4 h-4' />
                <span className='absolute right-full mr-2 bg-gray-100 px-2 py-1 rounded text-xs text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none'>
                  Clear message
                </span>
              </button>
            )}
            {isAITemplateLoading && (
              <div className='absolute inset-0 bg-white/50 flex items-center justify-center rounded-2xl z-10'>
                <LoadingSpinner iconClassName='w-6 h-6 text-custom-neutral-900 animate-spin' />
              </div>
            )}
          </div>
          <div className='flex justify-end'>
            {isGenerationViewActive && aiResponse && (
              <CharacterCounter message={message} type={isRejection ? 'rejection' : 'hold'} />
            )}
          </div>

          {isGenerationViewActive ? (
            <>
              {!aiResponse && !isAITemplateLoading && (
                <button
                  type='button'
                  data-testid='generate-template'
                  disabled={!prompt.length}
                  onClick={handleGenerateConversationTemplate}
                  className={cn(
                    'flex justify-center items-center gap-2 mb-4 h-14 rounded-2xl bg-[#153CB7] text-white text-sm font-semibold leading-[170%] w-full transition-all duration-200 ease-in-out',
                    !prompt.length && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <MagicWandIcon width='24px' height='24px' />
                  Generate template
                </button>
              )}
              {isAITemplateLoading && (
                <button
                  type='button'
                  onClick={handleCancelGeneratingTemplate}
                  className='grid justify-center w-full text-[#153CB7] text-sm leading-[170%] font-medium'
                >
                  Cancel Generating
                </button>
              )}
            </>
          ) : (
            <AiButton data-testid='generate-template' onClick={() => changeToAIGenerationPage(prompt.length > 0)}>
              Generate template
            </AiButton>
          )}
        </div>
      )}

      {isSendButtonVisible && canSendMessagesRole && (
        <button
          type='button'
          data-testid='send-patient'
          disabled={!!(isDisabled || !canSendMessagesRole)}
          onClick={handleSubmit}
          className={cn(
            'flex justify-center items-center gap-2 mb-4 h-14 rounded-2xl bg-[#153CB7] text-white text-sm font-semibold leading-[170%] w-full transition-all duration-200 ease-in-out',
            (isDisabled || !canSendMessagesRole) && 'opacity-50 cursor-not-allowed',
            isPending && 'cursor-wait opacity-70'
          )}
        >
          Send to patient
        </button>
      )}

      {!selectedFan && (
        <div className='flex items-center mb-4 gap-1 cursor-pointer'>
          <Checkbox
            id='follow-up-checkbox'
            checked={csContactPatient}
            disabled={isMps}
            onCheckedChange={checked => setCsNeedToContactPatient(checked === true)}
          />
          <label htmlFor='follow-up-checkbox' className='ml-2 text-sm text-[#1D1D1D]'>
            {isPanel ? 'CS team to contact patient' : 'CS needs to contact patient'}
          </label>
        </div>
      )}
      <button
        type='button'
        data-testid='cancel'
        onClick={handleCancel}
        className={cn(
          'flex h-14 w-full color-[#1D1D1D] items-center justify-center gap-4 rounded-2xl border border-custom-neutral-200 bg-white px-6 text-custom-neutral-600 text-sm font-semibold  hover:bg-custom-neutral-200 transition-colors duration-200 leading-[170%]',
          // The Clinical decision panel's Cancel matches its approve form's.
          isPanel && 'border-custom-neutral-500 text-[#153CB7]'
        )}
      >
        Cancel
      </button>
    </>
  )
}

const ConversationView = ({
  prompt,
  setPrompt,
  isRejection
}: Omit<PatientViewProps, 'isGenerationViewActive' | 'setIsGenerationViewActive' | 'aiResponse' | 'setAiResponse'>) => {
  const { user } = useUser()
  const medReqAction = useMedRequestAction({})
  const { setSidebarView } = useMedicationRequestStore()
  const action = medReqAction

  const [message, setMessage] = useState('')

  const medRequest = useCurrentMedication()

  const data = medRequest?.data

  const { data: conversation } = useGetConversationById({
    conversationId: isRejection ? (data?.rejectionConversationId ?? '') : (data?.latestFanConversationId ?? ''),
    enabled: !!(isRejection ? data?.rejectionConversationId : data?.latestFanConversationId)
  })

  const [aiResponse, setAiResponse] = useState('')

  const { mutateAsync: generateConversationTemplate, isPending: isAITemplateLoading } = useGenerateConversationTemplate(
    {
      type: isRejection ? 'rejection' : 'hold',
      medRequestId: data?.id ?? '',
      abortController: generationAbortController
    }
  )

  const { mutate: postReplyToConversation } = usePostReplyToConversation()
  const goNextRequest = useGoNextMedRequest()

  const isLastMessageFromPrescriber =
    conversation?.history[conversation?.history.length - 1]?.from?.propertyId === user?.id

  const isSendingDisabled = message.trim() === '' || data?.id === undefined

  const isPromptingDisabled = isAITemplateLoading || !prompt || isLastMessageFromPrescriber

  const [preview, setPreview] = useState<string | null>(null)
  const canSendMessagesRole = canSendMessages()

  const handleGenerateResponse = async () => {
    if (!canSendMessagesRole) return
    try {
      const res = await generateConversationTemplate({ prompt })
      setAiResponse(res)
      setMessage(res)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Sorry, we couldn’t generate a template for you at this time.'
      })
    }
  }

  const handleSendResponse = () => {
    if (!canSendMessagesRole || !data) return
    postReplyToConversation({
      conversationId: conversation?._id ?? '',
      message,
      medRequestId: data.id
    })
    setMessage('')
    setPrompt('')
    setAiResponse('')
    goNextRequest()
  }

  const handleCancel = () => {
    setSidebarView('navigation-menu')
    setMessage('')
  }

  if (!conversation) return null

  const showContactCSMessage = conversation.history.length > 3
  const disableAction = medRequest.data?.stage !== 'ID verified' || !canSendMessagesRole || medRequest.data?.locked

  return (
    <>
      {!canSendMessagesRole && (
        <div className='py-4 px-6 bg-[#FFF9E6] rounded-3xl border border-[#F4D03F] mb-4'>
          <p className='text-sm leading-[170%] text-[#B7950B] font-normal'>
            You have read-only access to this conversation. You cannot send messages or generate responses.
          </p>
        </div>
      )}
      <div className='overflow-y-auto max-h-[300px] flex flex-col-reverse gap-4 mb-4 pt-2'>
        {conversation?.history.toReversed().map((message, index) => {
          const isOwnMessage = message.from?.propertyId === user?.id
          return (
            <Message
              isOwnMessage={isOwnMessage}
              key={index}
              message={message.message}
              createdAt={message.createdAt}
              attachments={message.attachments}
              isUnread={!message.read}
              from={message.from}
              onAttachmentClick={setPreview}
            />
          )
        })}
      </div>
      {preview && <AttachmentPreview preview={preview} onClose={() => setPreview(null)} />}
      {showContactCSMessage && (
        <div className='py-4 px-6 bg-[#FAFAFB] rounded-3xl border border-[#E9EAED]'>
          <p className='text-sm leading-[170%] text-custom-neutral-500 font-normal'>
            Please contact CS to call the patient to resolve any remaining queries
          </p>
        </div>
      )}
      {!showContactCSMessage && !isLastMessageFromPrescriber && canSendMessagesRole && (
        <div className='p-4 rounded-2xl border border-[#E9EAED]'>
          <p className='mb-4 text-sm leading-[170%] text-[#4E545E] font-normal'>
            Please enter a text to generate a response for the patient
          </p>
          <TextareaWithLabel
            disabled={!!disableAction}
            className='h-[177px] rounded-2xl resize-none pt-3 mb-4'
            placeholder={aiResponse ? 'Reply to patient' : 'Add your prompt'}
            value={aiResponse ? message : prompt}
            onChange={e => (aiResponse ? setMessage(e.target.value) : setPrompt(e.target.value))}
            autoFocus
          />
          <button
            type='button'
            disabled={!!disableAction || (aiResponse ? isSendingDisabled : isPromptingDisabled)}
            onClick={aiResponse ? handleSendResponse : handleGenerateResponse}
            className={cn(
              'flex justify-center items-center gap-2 mb-4 h-14 rounded-2xl bg-[#153CB7] text-white text-sm font-semibold leading-[170%] w-full transition-all duration-200 ease-in-out',
              aiResponse
                ? isSendingDisabled && 'opacity-50 cursor-not-allowed'
                : isPromptingDisabled && 'opacity-50 cursor-not-allowed',
              (action.isPending || isAITemplateLoading) && 'cursor-wait opacity-70'
            )}
          >
            {aiResponse ? (
              'Send response'
            ) : (
              <span className='flex items-center gap-2'>
                Generate response
                <MagicWandIcon />
              </span>
            )}
          </button>
        </div>
      )}
      <button
        type='button'
        data-testid='cancel'
        onClick={handleCancel}
        className='disabled:opacity-50 mt-4 flex justify-center items-center h-14 rounded-2xl border border-[#E9EAED] text-sm font-semibold leading-[170%] w-full hover:bg-[#F5F6F7] transition-all duration-200 ease-in-out'
      >
        Cancel
      </button>
    </>
  )
}

type CSTeamViewProps = {
  isRejection?: boolean
} & PanelDecisionProps

export const CSTeamView = ({ isRejection, target, onDecide, onCancel, isSendDisabled }: CSTeamViewProps) => {
  const canSendMessagesRole = canSendMessages()
  const medRequest = useCurrentMedication(true)
  // In the Clinical decision panel the view decides `target`, possibly another item of the order.
  const data = target ?? medRequest?.data
  const isFanFlagEnabled = useFlag('conversation-fan') || data?.latestFanConversationId
  const isRejectionFlagEnabled = useFlag('rejection-fan') || data?.rejectionConversationId
  const isFlagEnabled = isRejection ? isRejectionFlagEnabled : isFanFlagEnabled

  const {
    setSidebarView,
    medicationDirection,
    contactMessage,
    setContactMessage,
    setRejectionReason,
    rejectionReason,
    notes
  } = useMedicationRequestStore()

  const message = isRejection ? rejectionReason : contactMessage
  const setMessage = isRejection ? setRejectionReason : setContactMessage

  const [template, setTemplate] = useState('')

  const { submit, isPending } = useRejectOrFollowUp(onDecide)

  const { templateOptions } = useQuestionnaire(data?.questionaire ?? '')
  const isPanel = !!onDecide
  const templateChoices = isRejection
    ? (templateOptions?.rejectionOptions ?? [])
    : [...(templateOptions?.fanOptions ?? []), ...(templateOptions?.onHoldOptions ?? [])]
  const selectedFan = !isRejection ? templateOptions?.fanOptions.find(option => option.value === template) : undefined

  // The opened request's summary, which only the page's own decision sends.
  const { prompt: scrExecutedPrompt, summary: scrSummaryResult } = useScrAiSummary({
    medicationRequestId: medRequest?.data?.id ?? ''
  })

  // As in PatientView: `isPending` briefly drops between a chain's mutations.
  const isDecisionSubmitting = useMedicationRequestStore(selectIsDecisionSubmitting)
  const isDisabled =
    (!selectedFan && !message.trim()) || isPending || isDecisionSubmitting || medRequest.data?.locked || isSendDisabled
  const [csNeedToContactPatient, setCsNeedToContactPatient] = useState(!!isRejection)
  // On MPS orders CS must always be emailed, so the flag is forced on and the checkbox locked.
  const isMps = isMpsMedRequest(data)
  const csContactPatient = isMps || csNeedToContactPatient

  const isTemplateDropdownDisabled = templateChoices.length === 0

  const handleSettingTemplate = (template: string) => {
    const fan = !isRejection && templateOptions?.fanOptions.find(option => option.value === template)
    setMessage(fan ? '' : template)
    setTemplate(template)
  }

  const prevRejectionReason = data?.reasonForRejection ?? ''
  const prevOnHoldReason = data?.reasonForHold ?? ''
  const previousReason = isRejection ? prevRejectionReason : prevOnHoldReason

  if (!data) return null

  const handleSubmit = () => {
    const reason = selectedFan ? `FAN consultation requested: ${selectedFan.label}` : message.trim()

    submit(
      data,
      isRejection
        ? {
            id: data.id,
            action: 'reject',
            reason,
            additionalNotes: notes,
            extraDirectionsForMedication: medicationDirection,
            scrExecutedPrompt: scrExecutedPrompt,
            scrSummaryResult: scrSummaryResult,
            csNeedToContactPatient: csContactPatient
          }
        : {
            id: data.id,
            action: 'hold',
            reason,
            startConversation: false,
            scrSummaryResult,
            scrExecutedPrompt
          },
      selectedFan?.value.slice(4)
    )
  }

  const handleCancel = () => {
    setMessage('')
    if (onCancel) return onCancel()
    setSidebarView('navigation-menu')
  }

  const fromName = previousReason.match(/\[(.*?)\]/)?.[1] ?? ''

  const dateMatch = previousReason.match(/\[(.*?)\]/g)?.[1]?.match(/\[(.*?)\]/)?.[1]
  const createdAt = dateMatch
    ? new Date(dateMatch.replace(/(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})/, '$3-$2-$1T$4:$5'))
    : new Date()

  const previousReasonMessage = previousReason.replace(/\[(.*?)\]/g, '')

  useEffect(() => {
    if (isRejection && previousReasonMessage && !message && !isRejectionFlagEnabled) {
      setMessage(previousReasonMessage)
    }
  }, [previousReasonMessage, isRejection, isRejectionFlagEnabled])

  return (
    <>
      {!canSendMessagesRole && (
        <div className='py-4 px-6 bg-[#FFF9E6] rounded-3xl border border-[#F4D03F] mb-4'>
          <p className='text-sm leading-[170%] text-[#B7950B] font-normal'>
            You have read-only access. You cannot {isRejection ? 'reject medication requests' : 'send CS team messages'}{' '}
            or submit actions.
          </p>
        </div>
      )}
      {isFlagEnabled && previousReason.length ? (
        <Message
          isCS
          isOwnMessage
          message={previousReasonMessage}
          from={{ propertyId: '', name: fromName }}
          createdAt={createdAt}
        />
      ) : null}

      {!isFlagEnabled && canSendMessagesRole && (
        <>
          <p className='mb-4 text-[#1D1D1D] font-medium leading-[170%]'>Select a template</p>
          <div className='mb-4'>
            <SingleCombobox
              isDisabled={isTemplateDropdownDisabled || !canSendMessagesRole || isPending}
              title='Templates'
              defaultValue={template}
              onValueChange={value => canSendMessagesRole && handleSettingTemplate(value)}
              options={templateChoices}
            />
          </div>
        </>
      )}

      {canSendMessagesRole && (
        <>
          {!selectedFan && (
            <>
              <TextareaWithLabel
                placeholder='Add your reason'
                className='h-[177px] rounded-2xl resize-none pt-3 mb-4'
                value={message}
                onChange={e => canSendMessagesRole && setMessage(e.target.value)}
                disabled={!canSendMessagesRole}
              />
              <div className='flex justify-end mb-4'>
                <CharacterCounter message={message} type={isRejection ? 'rejection' : 'hold'} />
              </div>
            </>
          )}
          <button
            type='button'
            data-testid='send-cs'
            disabled={!!(isDisabled || !canSendMessagesRole)}
            onClick={handleSubmit}
            className={cn(
              'flex justify-center items-center gap-2 mb-4 h-14 rounded-2xl bg-[#153CB7] text-white text-sm font-semibold leading-[170%] w-full transition-all duration-200 ease-in-out',
              (isDisabled || !canSendMessagesRole) && 'opacity-50 cursor-not-allowed',
              isPending && 'cursor-wait opacity-70'
            )}
          >
            {selectedFan ? 'Send to patient' : isFlagEnabled ? 'Send to CS Team' : 'Submit'}
          </button>
        </>
      )}
      {isRejection && (
        <div className='flex items-center mb-4 gap-1 cursor-pointer'>
          <Checkbox
            id='cs-follow-up-checkbox'
            checked={csContactPatient}
            disabled={isMps}
            onCheckedChange={checked => setCsNeedToContactPatient(checked === true)}
          />
          <label htmlFor='cs-follow-up-checkbox' className='ml-2 text-sm text-[#1D1D1D]'>
            {isPanel ? 'CS team to contact patient' : 'CS needs to contact patient'}
          </label>
        </div>
      )}
      <button
        type='button'
        data-testid='cancel'
        onClick={handleCancel}
        className={cn(
          'flex h-14 w-full color-[#1D1D1D] items-center justify-center gap-4 rounded-2xl border border-custom-neutral-200 bg-white px-6 text-custom-neutral-600 text-sm font-semibold  hover:bg-custom-neutral-200 transition-colors duration-200 leading-[170%]',
          // The Clinical decision panel's Cancel matches its approve form's.
          isPanel && 'border-custom-neutral-500 text-[#153CB7]'
        )}
      >
        Cancel
      </button>
    </>
  )
}

const ContactPatientSection = () => {
  const { setSidebarView } = useMedicationRequestStore.getState()
  const medRequest = useCurrentMedication(true)
  const data = medRequest?.data

  const isFanFlagEnabled = useFlag('conversation-fan') || data?.latestFanConversationId

  const { user } = useUser()

  const [prompt, setPrompt] = useState('')
  const [isGenerationViewActive, setIsGenerationViewActive] = useState(false)
  const [aiResponse, setAiResponse] = useState('')
  const [activeTab, setActiveTab] = useState<'patient' | 'cs-team'>('patient')

  const { data: conversation } = useGetConversationById({
    conversationId: data?.latestFanConversationId ?? '',
    enabled: !!data?.latestFanConversationId
  })

  const hasUnreadMessages = conversation?.history?.some(
    message => !message.read && message?.from?.propertyId !== user?.id
  )

  const isPatientTabActive = activeTab === 'patient'

  return (
    <div className='sticky top-[88px] max-w-[300px] overflow-y-auto max-h-[88vh] flex min-w-[25vw] flex-col rounded-2xl border border-custom-neutral-200 shadow-[0px_0px_1px_0px_rgba(113,128,150,0.04),0px_4px_8px_0px_rgba(113,128,150,0.08)]'>
      <div className='p-6'>
        <div className='mb-2 flex justify-between items-center'>
          <p className='text-custom-neutral-600 text-xl font-semibold leading-[133.4%]'>Follow up</p>
          <X
            className='w-6 h-6 text-custom-neutral-600 cursor-pointer'
            data-testid='close-icon'
            onClick={() => setSidebarView('navigation-menu')}
          />
        </div>
        <p className='text-custom-neutral-500 text-sm leading-[170%]'>
          Reach out to the patient if you have any questions.
        </p>
      </div>
      <div data-testid='contact-patient-section' className='px-6 pb-6'>
        {isFanFlagEnabled ? (
          <Tabs defaultValue={tabs[0]}>
            <TabsList className='w-full border-0 h-[75px] rounded-none p-0 text-[#080F30] mb-6 bg-white'>
              {tabs.map(tab => (
                <TabsTrigger
                  key={tab}
                  onClick={() => setActiveTab(tab as 'patient' | 'cs-team')}
                  className='w-full h-full text-base rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#080F30] data-[state=active]:shadow-none transition-all duration-200 ease-in-out data-[state=active]:font-semibold font-light'
                  value={tab}
                >
                  {tab === 'patient' ? 'Patient' : 'CS Team'}{' '}
                  {tab === 'patient' && hasUnreadMessages && <div className='w-3 h-3 ml-2 rounded-full bg-red-500' />}
                </TabsTrigger>
              ))}
            </TabsList>
            {isPatientTabActive ? (
              <PatientView
                isGenerationViewActive={isGenerationViewActive}
                setIsGenerationViewActive={setIsGenerationViewActive}
                aiResponse={aiResponse}
                setAiResponse={setAiResponse}
                prompt={prompt}
                setPrompt={setPrompt}
              />
            ) : (
              <CSTeamView />
            )}
          </Tabs>
        ) : (
          <CSTeamView />
        )}
      </div>
    </div>
  )
}

export default ContactPatientSection
